import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

// Models
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DEFAULT_CONTENT } from 'client/data/cms/content';

// Utils
import { TrackingHandler } from 'client/tracking/handler';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { buildBreadcrumbs, insertPricingCardsEntry } from 'site-modules/editorial/utils/best';
import { isEvArticle, TREEHOUSE_ARTICLE_ID } from 'site-modules/editorial/utils/ev';

// Constants
import { BestMap } from 'client/tracking/maps/editorial/best';
import { EngineTypeQuizMap } from 'client/tracking/maps/common/engine-type-quiz';
import { FULL_WIDTH_ENTRIES } from 'site-modules/editorial/constants/best';
import { RESEARCH_LINK_WIDGET_EDITORIAL_PATH } from 'site-modules/shared/constants/editorial/research-article';
import { EDITORIAL_HEADER_SRC_INLINE } from 'site-modules/shared/constants/editorial/editorial-header';

// Shared components
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { StaticLeaseLinkWidget } from 'site-modules/shared/components/static-lease-link-widget/static-lease-link-widget';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';

// Components
import { HighlightVideo } from 'site-modules/editorial/components/highlight-video/highlight-video';
import { ResearchEntry } from 'site-modules/editorial/components/best/research-entry/research-entry';
import { MainContent } from 'site-modules/editorial/components/best/main-content/main-content';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './best.scss';

const MainContentWithCompareTray = withCompareVehicleTray(MainContent);

function BestUI({
  content,
  pathname = null,
  isMobile = false,
  hasLinkWidget = false,
  category = null,
  articleId = '',
  linkWidgetContent = DEFAULT_CONTENT,
}) {
  const [videoModalData, setVideoModalData] = useState({});
  const [isVideoModalOpen, toggleVideoModalOpen] = useToggle(false);

  useEffect(() => {
    TrackingHandler.useMap({ ...BestMap, ...EngineTypeQuizMap });
  }, []);

  const onToggleVideoModal = useCallback(newVideoModalData => {
    toggleVideoModalOpen();
    if (get(newVideoModalData, 'videoId')) {
      setVideoModalData(newVideoModalData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEvArticlePage = isEvArticle({ slug: category, categories: content.categories });
  const isMonthly199Article = content.id.includes('/car-leasing/monthly-199-lease-deals');

  const { published, updated, title, subtitle } = content;
  const canonical = getCanonical(content);
  const mainEntries = content.child('main').children();
  const rightRailEntries = content.child('right-rail').children();
  const footerEntries = insertPricingCardsEntry(content.child('footer').children());
  const configEntry = content.child('config');
  const breadcrumbsData = buildBreadcrumbs(configEntry, title, pathname);

  // highlight video
  const highlightVideoEntry = content.child('highlight-video');
  const hasHighlightVideo = !!highlightVideoEntry.metadata('videoId').value();

  // anchor nav
  const subnavEntry = content.child('subnav');
  const anchorNavTitle = subnavEntry.metadata('subnavTitle').value();
  const anchorNavList = subnavEntry.links().map(({ href, title: name }) => ({
    name,
    hash: href.replace('#', ''),
  }));

  const entriesProps = {
    title,
    canonical,
    pageTitle: title,
    pathname,
    contentId: content.id,
  };

  const isCarLeasingPage = pathname.includes('car-leasing');

  const rightRail = rightRailEntries.map((entry, index) => {
    const key = `right-rail-${entry.id}-${index}`;
    return (
      <ResearchEntry key={key} entry={entry} isMobile={isMobile} entriesProps={entriesProps} isRightRail={!isMobile} />
    );
  });

  return (
    <main id="main-content" tabIndex={-1} className="best pb-2 pos-r" data-ad-zone={articleId}>
      <div
        style={{
          backgroundImage: `url('${EDITORIAL_HEADER_SRC_INLINE[isMobile]}')`,
          backgroundSize: '100% 100%',
        }}
      >
        <Container className="mb-1 pb-2 pb-md-3_5">
          <Breadcrumbs className="ps-0 text-white mb-1_5 mb-md-2" data={breadcrumbsData} />
          <Row>
            <Col xs={12} lg={9}>
              <h1
                className={classnames('text-white fw-bold', {
                  'mb-0-75': !!subtitle,
                  'size-36': !isMobile,
                  'size-24': isMobile,
                })}
              >
                {title}
              </h1>
              {!!subtitle && <h2 className="size-16 text-white fw-medium">{subtitle}</h2>}
            </Col>
          </Row>
        </Container>
      </div>
      <Container>
        <Row>
          <Col xs={12} lg={8}>
            <div className="mb-1_5">{dateFormat(updated || published, 'mmmm dS, yyyy')}</div>
            {/* main */}
            <MainContentWithCompareTray
              entries={mainEntries}
              isMobile={isMobile}
              entriesProps={entriesProps}
              category={category}
              anchorNavTitle={anchorNavTitle}
              anchorNavList={anchorNavList}
            />
            <Experiment name="ads-12460_dfp" showDefault>
              <Recipe name="ctrl" isDefault>
                <MinHeightWrapper componentType="SPONSORED_CARS">
                  <SponsoredCars showLargeArrows verticalOffset="50%" />
                </MinHeightWrapper>
              </Recipe>
              <Recipe name="chal">
                <MinHeightWrapper
                  componentType="SPONSORED_CARS_STACKED"
                  style={isMobile ? null : { maxWidth: '744px' }}
                >
                  <SponsoredCars showLargeArrows verticalOffset="50%" showStackedAds />
                </MinHeightWrapper>
              </Recipe>
            </Experiment>
          </Col>
          <Col xs={12} lg={4} className="d-flex flex-column">
            {/* right-rail */}
            {hasHighlightVideo && (
              <HighlightVideo entry={highlightVideoEntry} openVideoModal={onToggleVideoModal} isMobile={false} />
            )}
            {!isMobile && <div className="d-flex flex-column right-rail-entries">{rightRail}</div>}
            {isMobile && rightRail}
          </Col>
          {/* footer */}
          {footerEntries.map((entry, index) => {
            const key = `footer-${entry.id}-${index}`;
            return (
              <Col xs={12} lg={FULL_WIDTH_ENTRIES.includes(entry.id) ? null : 8} key={key}>
                <ResearchEntry
                  entry={entry}
                  isMobile={isMobile}
                  entriesProps={entriesProps}
                  hasSeparator={index !== footerEntries.length - 1}
                  hasTreehousePromo={TREEHOUSE_ARTICLE_ID.includes(articleId)}
                />
              </Col>
            );
          })}
          {isEvArticlePage && (
            <Col xs={12} lg={8}>
              <hr className="mt-1_5 mb-2" />
              <EngineTypeQuiz />
            </Col>
          )}
          {isMonthly199Article && (
            <Col xs={12} lg={8}>
              <hr className="mt-1_5 mb-2" />
              <StaticLeaseLinkWidget />
            </Col>
          )}
        </Row>
        <YoutubeVideoModalAsync
          onToggleModal={onToggleVideoModal}
          isOpen={isVideoModalOpen}
          videoId={videoModalData.videoId}
          creativeId={videoModalData.creativeId}
          videoTitle={videoModalData.videoTitle}
        />
        {hasLinkWidget && (
          <Fragment>
            <hr className="mt-1_5 mb-2" />
            <PageLinksWidget divideSections={false} title="Related information" />
          </Fragment>
        )}
        {isCarLeasingPage && (
          <ErrorBoundary>
            <Row>
              <Col xs={12} className="pb-3">
                <hr className="mt-0 mb-2" />
                <VehicleShowcase content={linkWidgetContent.child('home-vehicle-showcase-v2')} bottomBorder={false} />
              </Col>
            </Row>
          </ErrorBoundary>
        )}
      </Container>
    </main>
  );
}

BestUI.propTypes = {
  content: CmsEntities.Content.isRequired,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  hasLinkWidget: PropTypes.bool,
  category: PropTypes.string,
  articleId: PropTypes.string,
  linkWidgetContent: CmsEntities.Content,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pathname: get(state, 'pageContext.location.pathname'),
});

export const Best = connect(mapStateToProps)(
  connectToModel(BestUI, {
    linkWidgetContent: bindToContent(RESEARCH_LINK_WIDGET_EDITORIAL_PATH),
  })
);
